import { useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import type { OpenQuestionsList } from '@localagent/sdk';
import { setOpenQuestionsAtom } from '@localagent/sdk/react';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { openQuestionsErrorAtom } from '../stores/chat/atoms';
import {
  bridgeEventStreamAtom,
  daemonPushFrameStreamAtom,
  sdkConfiguredAtom,
} from '../stores/sdk/atoms';

function asError(err: unknown): Error {
  return err instanceof Error ? err : new Error(String(err));
}

export function useOpenQuestionsHandle(): void {
  const ready = useAtomValue(sdkConfiguredAtom);
  const authenticated = useAtomValue(holdsIdentityAtom);
  const webStream = useAtomValue(bridgeEventStreamAtom);
  const daemonStream = useAtomValue(daemonPushFrameStreamAtom);
  const publish = useSetAtom(setOpenQuestionsAtom);
  const setError = useSetAtom(openQuestionsErrorAtom);
  const [list, setList] = useState<OpenQuestionsList | null>(null);

  useEffect(() => {
    setError(null);
    if (!ready || !authenticated) {
      setList(null);
      publish([]);
      return;
    }
    let cancelled = false;
    let offChange: (() => void) | undefined;
    getSDK()
      .questions.connect()
      .then((opened) => {
        if (cancelled) return;
        offChange = opened.onChange((items) => {
          publish(items);
          setError(null);
        });
        publish(opened.items);
        setList(opened);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(asError(err));
      });
    return () => {
      cancelled = true;
      offChange?.();
      setList(null);
    };
  }, [ready, authenticated, publish, setError]);

  useEffect(() => {
    if (!list) return;
    const stream = webStream ?? daemonStream;
    if (!stream) return;
    return list.follow(stream, (err: unknown) => {
      setError(asError(err));
    });
  }, [list, webStream, daemonStream, setError]);
}
